/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useMemo, useEffect, ElementType } from "react";
import {
  MdNavigateNext as NextIcon,
  MdNavigateBefore as PreviousIcon
} from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import { v4 as uniqueID } from "uuid";

import Button from "components/Button";
import Label from "components/Label";

import { useRecoilState } from "recoil";
import styles from "./styles.module.scss";
import { Step } from "./types";
import { flowSharedState } from "./atoms";

type Props = {
  Progress?: ElementType<{
    forward: boolean;
    setCurrentStep(newStep: number): void;
    steps: Step[];
    currentStep: number;
  }>;
  steps: Step[];
  withoutPreviousStepButton?: boolean;
  withoutNextStepButton?: boolean;
  className?: string;
  showProgress?: boolean;
  contentClassName?: string;
  initialFlowState?: { [key: string]: string };
};

const Flow: FC<Props> = ({
  steps,
  withoutNextStepButton,
  withoutPreviousStepButton,
  className,
  showProgress,
  contentClassName,
  initialFlowState = {},
  Progress
}: Props) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [forward, setForward] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [previousDisabled, setPreviousDisabled] = useState(false);

  const uid = useMemo(() => uniqueID(), []);

  const [, setFlowState] = useRecoilState(flowSharedState(uid));

  useEffect(() => {
    setFlowState(initialFlowState);
  }, []);

  const totalSteps = steps.length;

  const withoutHeader = withoutNextStepButton && withoutPreviousStepButton;

  const {
    Component,
    title,
    subtitle,
    onGoToNextStep,
    onGoToPreviousStep,
    key,
    onFinish,
    ...stepProps
  } = steps[currentStep];

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
    setForward(true);
    onGoToNextStep?.();
  };

  const previousStep = () => {
    setCurrentStep(currentStep - 1);
    setForward(false);
    onGoToPreviousStep?.();
  };

  const variants = {
    initial: (customForward: boolean) => ({
      opacity: 0,
      x: customForward ? "100%" : "-100%"
    }),
    exit: (customForward: boolean) => ({
      x: customForward ? "-100%" : "100%",
      opacity: 0
    }),
    active: { x: "0px", opacity: 1 }
  };

  return (
    <div className={`${styles.container} ${className}`}>
      {showProgress && Progress && (
        <Progress
          forward={forward}
          setCurrentStep={(newStep: number) => {
            if (newStep > currentStep) setForward(true);
            else setForward(false);
            setCurrentStep(newStep);
          }}
          steps={steps}
          currentStep={currentStep}
        />
      )}
      {!withoutHeader && (
        <div className={styles.header}>
          {!withoutPreviousStepButton && (
            <Button
              className={styles.previousButton}
              variant="text"
              onClick={previousStep}
              Icon={<PreviousIcon className={styles.icon} />}
              disabled={previousDisabled || currentStep === 0}
            />
          )}
          {!withoutNextStepButton && (
            <Button
              className={styles.nextButton}
              variant="primary"
              onClick={nextStep}
              Icon={<NextIcon className={styles.icon} />}
              iconAfter
              disabled={nextDisabled || currentStep === totalSteps - 1}
            />
          )}
        </div>
      )}
      <AnimatePresence initial={false} custom={forward} exitBeforeEnter>
        <motion.div
          custom={forward}
          className={`${styles.content} ${contentClassName}`}
          key={key}
          animate="active"
          exit="exit"
          initial="initial"
          transition={{ ease: "easeInOut", duration: 0.7 }}
          variants={variants}
        >
          {(title || subtitle) && (
            <div className={styles.headerInfo}>
              {title && (
                <Label
                  className={styles.titles}
                  variant="centered-h5"
                  color="primary"
                >
                  {title}
                </Label>
              )}
              {subtitle && (
                <Label className={styles.titles} variant="centered-light">
                  {subtitle}
                </Label>
              )}
            </div>
          )}
          <Component
            onFinish={onFinish}
            sharedFlowState={flowSharedState(uid)}
            nextStep={nextStep}
            previousStep={previousStep}
            setNextButtonDisabled={setNextDisabled}
            setPreviousbuttonDisabled={setPreviousDisabled}
            {...stepProps}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

Flow.defaultProps = {
  withoutNextStepButton: false,
  withoutPreviousStepButton: false,
  className: "",
  showProgress: false,
  contentClassName: undefined,
  initialFlowState: {},
  Progress: undefined
};

export default Flow;
